import { useMemo } from 'react'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'

import { Text } from '../../../Components'
import { CHART_ANIMATION_PIE, CHART_TOOLTIP_STYLE } from '../../../Components/Charts/chartTheme'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { getStateColor, STATE_ENUM } from '../../Gigi/GigiList/Functions'

const language = cap.language.state || 'IT'
const t = translations[language]

/**
 * Grafico a ciambella che mostra la distribuzione dei processi per stato
 * @param {{ processes?: Array<{ state: number }>, stateCounts?: Record<string, number>, totalCount?: number, onStateClick?: (stateValue: number) => void }} props
 * @returns {JSX.Element|null}
 */
export const ProcessStateChart = ({
  processes = [],
  stateCounts: stateCountsProp,
  totalCount: totalCountProp,
  onStateClick
}) => {
  const { chartData, total } = useMemo(() => {
    if (stateCountsProp && typeof stateCountsProp === 'object') {
      const tot = Object.values(stateCountsProp).reduce((a, v) => a + Number(v || 0), 0)
      const data = Object.entries(stateCountsProp)
        .map(([state, count]) => {
          const stateNum = parseInt(state, 10)
          const entry = STATE_ENUM.find((e) => e.value === stateNum)
          return {
            name: entry?.label || `${t.pfStato} ${state}`,
            value: Number(count) || 0,
            color: getStateColor(stateNum),
            stateValue: stateNum
          }
        })
        .filter((d) => d.value > 0)
        .sort((a, b) => b.value - a.value)
      return { chartData: data, total: totalCountProp ?? tot }
    }

    const counts = {}
    for (const p of processes) {
      counts[p.state] = (counts[p.state] || 0) + 1
    }
    const data = Object.entries(counts)
      .map(([state, count]) => {
        const stateNum = parseInt(state, 10)
        const entry = STATE_ENUM.find((e) => e.value === stateNum)
        return {
          name: entry?.label || `${t.pfStato} ${state}`,
          value: count,
          color: getStateColor(stateNum),
          stateValue: stateNum
        }
      })
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value)
    return { chartData: data, total: processes.length }
  }, [processes, stateCountsProp, totalCountProp])

  if (!total && !chartData.length) return null

  return (
    <div className="ncard">
      <div className="ncard__head">
        <div className="ncard__ico">
          <i className="pi pi-chart-pie" style={{ fontSize: 16 }} />
        </div>
        <div>
          <h3 className="ncard__title">{t.pfStatoProcessi}</h3>
          <div className="ncard__sub">
            {t.totale} {total.toLocaleString('it-IT')}
          </div>
        </div>
      </div>
      <div
        className="ncard__body"
        style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}
      >
        <div style={{ position: 'relative', flexShrink: 0, minWidth: 180, height: 200, flex: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={3}
                dataKey="value"
                animationDuration={CHART_ANIMATION_PIE.duration}
                animationEasing={CHART_ANIMATION_PIE.easing}
                stroke="none"
                onClick={(_, index) => {
                  const state = chartData[index]?.stateValue
                  if (state === 5 && onStateClick) onStateClick(state)
                }}
              >
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name) => [value.toLocaleString('it-IT'), name]}
                contentStyle={CHART_TOOLTIP_STYLE}
              />
            </PieChart>
          </ResponsiveContainer>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none'
            }}
          >
            <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em' }}>
              {total.toLocaleString('it-IT')}
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-500)' }}>{t.pfProcessiLower}</div>
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10, minWidth: 160 }}>
          {chartData.map((d) => {
            const isClickable = onStateClick && d.stateValue === 5
            return (
              <div
                key={d.name}
                role={isClickable ? 'button' : undefined}
                tabIndex={isClickable ? 0 : undefined}
                onClick={() => isClickable && onStateClick(d.stateValue)}
                onKeyDown={(e) => e.key === 'Enter' && isClickable && onStateClick(d.stateValue)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  fontSize: 13,
                  cursor: isClickable ? 'pointer' : undefined,
                  borderRadius: 6,
                  padding: '4px 8px',
                  margin: '0 -8px',
                  transition: 'background 0.15s'
                }}
                className={isClickable ? 'perf-state-legend-item' : undefined}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 3,
                    background: d.color,
                    flexShrink: 0
                  }}
                />
                <span style={{ flex: 1, color: 'var(--ink-700)' }}>{d.name}</span>
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    color: 'var(--ink-900)',
                    fontWeight: 600
                  }}
                >
                  {d.value.toLocaleString('it-IT')}
                </span>
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    color: 'var(--ink-500)',
                    fontSize: 11.5,
                    minWidth: 46,
                    textAlign: 'right'
                  }}
                >
                  {total ? ((d.value / total) * 100).toFixed(1) : 0}%
                </span>
                {isClickable && (
                  <i
                    className="pi pi-chevron-right"
                    style={{ fontSize: 10, color: 'var(--ink-400)' }}
                  />
                )}
              </div>
            )
          })}
        </div>
      </div>
      <Text
        value={t.pfDatiCacheProcessOverview}
        size={10}
        style={{ color: 'var(--ink-500)', padding: '0 22px 14px' }}
      />
    </div>
  )
}
